import { useTranslation } from "react-i18next";
import { AlertTriangle, CalendarCheck, Euro, Heart, UserPlus } from "lucide-react";

import Button from "../../../../../shared/ui/button/Button";
import {
    DateRangeFilter,
    GranularityToggle,
    ReportChartCard,
    StatTile,
    TimeSeriesChart,
    formatCount,
    formatMoney,
    formatMoneyRounded,
    formatPeriodFull,
    formatPeriodShort,
    toAmount,
    useChartTokens,
    type ReportGranularity,
} from "../../../../../shared/components/reports";
import {
    useCenterMembers,
    useCenterPaymentsEnabled,
    useCenterReports,
    useCenterTimeReports,
} from "../../../../center";
import PaymentStatusBreakdown from "./PaymentStatusBreakdown";

interface Props {
    centerId: string;
    from: string;
    to: string;
    granularity: ReportGranularity;
    onFromChange: (day: string) => void;
    onToChange: (day: string) => void;
    onGranularityChange: (value: ReportGranularity) => void;
    onClear: () => void;
}

const sum = <T,>(rows: T[], pick: (row: T) => number) =>
    rows.reduce((total, row) => total + pick(row), 0);

/**
 * The centre as a whole: how much work, how many people, how much money —
 * over time. Nobody is named here; who did the work is the Therapists
 * section's business.
 */
export default function CenterOverviewReports({
    centerId,
    from,
    to,
    granularity,
    onFromChange,
    onToChange,
    onGranularityChange,
    onClear,
}: Props) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const tokens = useChartTokens();
    const { members } = useCenterMembers(centerId);
    // No money on this page while the centre has payments switched off.
    const { enabled: paymentsEnabled } = useCenterPaymentsEnabled(centerId);
    const range = { start: `${from}T00:00:00`, end: `${to}T23:59:59` };
    const { sessions } = useCenterReports(centerId, range);
    const {
        activity,
        paymentPeriods,
        paymentStatuses,
        tooManyPeriods,
        loading: timeLoading,
    } = useCenterTimeReports(centerId, range, granularity);

    const count = (value: number) => formatCount(value, locale);
    const money = (value: number) => formatMoney(value, locale);
    const shortLabel = (period: string) => formatPeriodShort(period, granularity, locale);
    const fullLabel = (period: string) => formatPeriodFull(period, granularity, locale);

    // Today's team, not the window's: who is in the centre and treats.
    const activeTherapists = members.filter(
        (m) => m.status === "active" && m.is_practising_therapist
    ).length;
    const therapistsWithSessions = sessions.filter((row) => row.sessions > 0).length;

    const totalSessions = sum(activity, (p) => p.sessions);
    const totalNewPatients = sum(activity, (p) => p.new_patients);
    const peopleSeen = sum(sessions, (row) => row.patients);
    const hours = sum(sessions, (row) => row.minutes) / 60;
    const paid = sum(paymentPeriods, (p) => toAmount(p.paid));
    const pending = sum(paymentPeriods, (p) => toAmount(p.pending));

    const activityCategories = activity.map((p) => shortLabel(p.period));
    const activityFull = activity.map((p) => fullLabel(p.period));
    const paymentCategories = paymentPeriods.map((p) => shortLabel(p.period));
    const paymentFull = paymentPeriods.map((p) => fullLabel(p.period));
    const perPeriod = t(`center.reports.per.${granularity}`);
    const showTimeCharts = !timeLoading && !tooManyPeriods;

    return (
        <>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <DateRangeFilter
                    idPrefix="reports"
                    from={from}
                    to={to}
                    onFromChange={onFromChange}
                    onToChange={onToChange}
                    onClear={onClear}
                >
                    <GranularityToggle value={granularity} onChange={onGranularityChange} />
                </DateRangeFilter>

                {tooManyPeriods && (
                    <div
                        role="status"
                        className="mb-5 flex flex-col gap-3 rounded-xl border border-status-warning/30 bg-status-warning-tint p-4 text-sm text-status-warning-on-tint sm:flex-row sm:items-center sm:justify-between"
                    >
                        <span className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                            {t("center.reports.tooManyPeriods")}
                        </span>
                        <Button size="sm" variant="outline" onClick={() => onGranularityChange("month")}>
                            {t("center.reports.groupByMonth")}
                        </Button>
                    </div>
                )}

                <div
                    className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${paymentsEnabled ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}
                >
                    <StatTile
                        icon={Heart}
                        label={t("center.reports.activeTherapists")}
                        value={count(activeTherapists)}
                        caption={t("center.reports.withSessions", {
                            count: therapistsWithSessions,
                            formatted: count(therapistsWithSessions),
                        })}
                    />
                    <StatTile
                        icon={CalendarCheck}
                        label={t("center.reports.sessions")}
                        value={showTimeCharts ? count(totalSessions) : "—"}
                        caption={t("center.reports.hoursOfSession", {
                            hours: hours.toLocaleString(locale, { maximumFractionDigits: 1 }),
                        })}
                    />
                    <StatTile
                        icon={UserPlus}
                        label={t("center.reports.newPatients")}
                        value={showTimeCharts ? count(totalNewPatients) : "—"}
                        caption={t("center.reports.peopleSeenCaption", {
                            count: peopleSeen,
                            formatted: count(peopleSeen),
                        })}
                    />
                    {paymentsEnabled && (
                        <StatTile
                            icon={Euro}
                            label={t("center.reports.collected")}
                            value={showTimeCharts ? money(paid) : "—"}
                            caption={
                                showTimeCharts
                                    ? t("center.reports.pendingCaption", { amount: money(pending) })
                                    : undefined
                            }
                        />
                    )}
                </div>
            </section>

            {timeLoading && <p className="text-sm text-content-secondary">{t("common.loading")}</p>}

            {showTimeCharts && (
                <>
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                        <ReportChartCard
                            title={t("center.reports.sessionsOverTime")}
                            description={t("center.reports.sessionsOverTimeHint", { per: perPeriod })}
                            empty={totalSessions === 0}
                            table={{
                                columns: [t("center.reports.period"), t("center.reports.sessions")],
                                rows: activity.map((p, i) => [activityFull[i], count(p.sessions)]),
                            }}
                        >
                            <TimeSeriesChart
                                kind="bar"
                                categories={activityCategories}
                                fullLabels={activityFull}
                                series={[
                                    {
                                        name: t("center.reports.sessions"),
                                        data: activity.map((p) => p.sessions),
                                        color: tokens.series1,
                                    },
                                ]}
                                tokens={tokens}
                                formatValue={count}
                                ariaLabel={t("center.reports.sessionsOverTime")}
                            />
                        </ReportChartCard>

                        <ReportChartCard
                            title={t("center.reports.patientsOverTime")}
                            description={t("center.reports.patientsOverTimeHint", { per: perPeriod })}
                            empty={activity.every((p) => p.patients_seen === 0 && p.new_patients === 0)}
                            table={{
                                columns: [
                                    t("center.reports.period"),
                                    t("center.reports.patientsSeen"),
                                    t("center.reports.newPatients"),
                                ],
                                rows: activity.map((p, i) => [
                                    activityFull[i],
                                    count(p.patients_seen),
                                    count(p.new_patients),
                                ]),
                            }}
                        >
                            <TimeSeriesChart
                                kind="line"
                                categories={activityCategories}
                                fullLabels={activityFull}
                                series={[
                                    {
                                        name: t("center.reports.patientsSeen"),
                                        data: activity.map((p) => p.patients_seen),
                                        color: tokens.series1,
                                    },
                                    {
                                        name: t("center.reports.newPatients"),
                                        data: activity.map((p) => p.new_patients),
                                        color: tokens.series2,
                                    },
                                ]}
                                tokens={tokens}
                                formatValue={count}
                                ariaLabel={t("center.reports.patientsOverTime")}
                            />
                        </ReportChartCard>
                    </div>

                    {paymentsEnabled && (
                        <ReportChartCard
                            title={t("center.reports.paymentsTitle")}
                            description={t("center.reports.paymentsHint", { per: perPeriod })}
                            empty={paymentStatuses.length === 0}
                            table={{
                                columns: [
                                    t("center.reports.period"),
                                    t("center.payments.states.paid"),
                                    t("center.payments.states.pending"),
                                ],
                                rows: paymentPeriods.map((p, i) => [
                                    paymentFull[i],
                                    money(toAmount(p.paid)),
                                    money(toAmount(p.pending)),
                                ]),
                            }}
                        >
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                                <div className="lg:col-span-2">
                                    {paid + pending > 0 ? (
                                        <TimeSeriesChart
                                            kind="bar"
                                            stacked
                                            categories={paymentCategories}
                                            fullLabels={paymentFull}
                                            series={[
                                                {
                                                    name: t("center.payments.states.paid"),
                                                    data: paymentPeriods.map((p) => toAmount(p.paid)),
                                                    color: tokens.success,
                                                },
                                                {
                                                    name: t("center.payments.states.pending"),
                                                    data: paymentPeriods.map((p) => toAmount(p.pending)),
                                                    color: tokens.warning,
                                                },
                                            ]}
                                            tokens={tokens}
                                            formatValue={money}
                                            formatAxis={(value) => formatMoneyRounded(value, locale)}
                                            ariaLabel={t("center.reports.paymentsTitle")}
                                        />
                                    ) : (
                                        <p className="flex h-full min-h-[120px] items-center justify-center rounded-xl bg-surface-sunken text-sm text-content-tertiary">
                                            {t("center.reports.noMoneyMoved")}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-content-tertiary">
                                        {t("center.reports.byStatus")}
                                    </h4>
                                    <PaymentStatusBreakdown statuses={paymentStatuses} tokens={tokens} />
                                </div>
                            </div>
                        </ReportChartCard>
                    )}
                </>
            )}
        </>
    );
}
